---
id: "summary-rl"
title: "Summary-RL"
excerpt: "Qwen 2.5 14B learns to generate better summaries of documents than Sonnet 4, gpt-4.1 and gemini-2.5-flash-preview."
date: "Jun 25, 2025"
category: "Technical"
readTime: "10"
author: "Andie Jones"
featured: false
draft: false
---

import { ExampleQuestions } from "./ExampleQuestions";
import { Callout } from "../../../components/blog/Callout";
import { LightboxImage } from "../../../components/blog/LightboxImage";
import { ExampleTrajectories } from "./ExampleTrajectories";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../../components/ui/accordion";
import SummaryDemo from "../../../demos/SummaryDemo";

Quickly summarizing large amounts of data is comfortably within the capabilities of modern LLMs. However, it’s difficult to define what a good summary looks like. The kind of information an LLM thinks is important might be completely different from what you care about.

To overcome this and other challenges, we embarked on a quest to train the ultimate summarizer LLM! Through reinforcement learning using the [ART](https://github.com/OpenPipe/art) library, we taught **Qwen 2.5 14B** to write document summaries for ServiceNow's [Repliqa benchmark](https://huggingface.co/datasets/ServiceNow/repliqa) that are more comprehensive than those generated by `Sonnet 4`, `gpt-4.1` and `gemini-2.5-flash`, while enforcing a strict 350 word limit. In addition to its higher benchmark scores, the resulting model is significantly faster and cheaper than off-the-shelf alternatives.

<div className="flex flex-col md:flex-row gap-4 items-center">
  <LightboxImage
    src="/blog-images/summary-rl/percentage-comparison.svg"
    alt="Accuracy comparison"
    caption="Summary RL outperforms Sonnet 4, generating summaries that can answer 85% of questions about the original document."
    captionPosition="bottom"
  />
</div>

We open-sourced all our [training code](https://github.com/OpenPipe/Summary-RL), which can be used as a template for your own RL projects. Later in this post, we also include a demo that allows you to generate your own summaries using our model. We'll cover the details of how we set up an environment, how we trained our model, and the final results. We'll also share lessons learned along the way.

Let's get started!

## **Defining a “Good” Summary**

Before setting out to train our model, we need to define what makes a summary good. This question is a difficult one, because summary quality is subjective and hard to pinpoint. To maintain consistency across diverse document types, let's optimize for summary comprehensiveness. In other words, **good summaries answer more questions about the document than bad summaries**.

This is a good metric, because it's easy to understand and maps well to our subjective preference. But how can we turn this definition into an objective reward function, and how can we train our model to get better at it?

## **Rewarding the Model**

To properly train our model to generate good summaries, we need a few components in place:

1. A judge LLM that answers questions about a document given just its summary.
2. A reward function that assigns a score to each summary.
3. A dataset of document-question-answer triplets.

We'll start with the first two components.

### **Judge LLM**

We'll use `gemini-2.5-flash-preview` because it's fast, cheap, and well-suited to needle-in-a-haystack problems. First, we'll provide the judge LLM with a document's generated summary and each question from its dataset individually, and the judge LLM will do its best to answer every question. Then we'll again use `gemini-2.5-flash-preview` to determine the fraction of answers that match the correct answer from the dataset.

### **Reward Function**

Let's formalize our reward function. To encourage summaries that answer more questions from the original document, we should give higher rewards to summaries that answer a larger fraction of questions.

However, with this check alone, our model may learn to reproduce whole documents in their entirety (since the original document is guaranteed to answer every question). To prevent regurgitation, let's enforce a strict 350 word limit on our summaries. If the summarizer produces more than 350 words, the summary will be given a score of `0`, even if it does answer every question we're interested in.

So to _summarize_, our reward function will be the following:

- If a summary is less than 350 words, it will be given a reward of `<questions-answered-correctly>/<total-questions>`, which will always be between `0` and `1`.
- If a summary is more than 350 words, it will be given a reward of `0`.

We could add other criteria, like bonus points for summaries that are even shorter than 350 words and still answer every question. But for the sake of simplicity let's limit ourselves to these two metrics. More on this in the [Lessons Learned](#lessons-learned) section.

Now we need to find a dataset that contains document-question-answer triplets.

## **Data**

We used ServiceNow’s [Repliqa dataset](https://huggingface.co/datasets/ServiceNow/repliqa), which contains synthetic documents that describe **realistic but fake information** to avoid the judge model answering from its own memory.

Each document is around 1000 words and comes with 5 questions and 5 corresponding golden answers, including some “distractor” questions that ask about information that doesn’t exist in the document provided (the golden answer in these cases is “I don’t know”).

We did prefiltering to remove documents for which our judge model was not able to answer all questions given the full document. This dataset is designed to be challenging for LLMs, and we found that `gemini-2.5-flash-preview` could only answer around 85% of questions given the full document. Some of these ambiguously written questions might be within the capabilities of a smarter judge model like Sonnet 4, but making the upgrade would balloon training costs.

Here are some example documents along with one of the five questions in each document's list.

<ExampleQuestions />

## **Initial Benchmarks**

Given the filtered dataset, let's see how top models like Sonnet 4 perform. First, we'll ask them to generate summaries around 300 words in length for these documents, and then hand these summaries off to our judge.

| Model                    | Percentage of Questions Answered Correctly |
| ------------------------ | ------------------------------------------ |
| Sonnet 4                 | **72%**                                    |
| Gemini 2.5 Flash Preview | 69%                                        |
| GPT-4o                   | 66%                                        |
| GPT-4.1                  | 51%                                        |
| Qwen 2.5 14B             | 43%                                        |

Sonnet 4's summaries take the cake with a score of **72%**, only 28% below the perfect score of 100% obtained by giving full documents to the judge. By contrast, Qwen 2.5 14B's summaries are only able to answer **43%** of questions. This is a significant gap, but we're optimistic that RL can help us close it!

## Training

We'll use `Qwen 2.5 14B` as our base model. Initially, this model generates somewhat unhelpful overviews without much detail filled with transition words like "Additionally", "Similarly" and "For example".

We'll use the open source [ART library](https://github.com/OpenPipe/ART) for our training and evaluations. ART (Agent Reinforcement Trainer) is purpose-built to make it easy to train real-world multi-turn agents using [Group Relative Policy Optimization](https://rlhfbook.com/c/11-policy-gradients.html#group-relative-policy-optimization) (GRPO), but it also works great for single-turn tasks like this one.

The GRPO training loop is quite simple. For this task, we do the following:

1. Load a batch of 10 documents (and their question/answer pairs) from our dataset.
2. For each document, generate 10 summaries.
3. For each summary:
   <ol
     className="alpha-tight"
     style={{ listStyleType: "lower-alpha", padding: "0" }}
   >
     <li>
       Ask the judge LLM to answer each of the 5 questions using the summary.
     </li>
     <li>Check how many of the 5 generated answers match the correct ones.</li>
     <li>
       Assign a reward according to our [reward function](#reward-function).
     </li>
   </ol>
4. Use all 10 groups of 10 summaries (100 summaries in total) along with their rewards to calculate the loss using the GRPO formula and update the model.
5. Ask the model to generate summaries on a validation set of 25 documents to make sure the model is improving.
6. Goto 1 until the model stops improving on our validation set.

## **Results**

As you can see in the training curve below, our model is able to answer **85%** of questions, which is a significant improvement over the **43%** it started with.

<LightboxImage
  src="/blog-images/summary-rl/percentage-training-progress.svg"
  alt="Training curve"
  caption="Our final run has a pretty good-looking training curve, but the first run we tried didn't look this way!"
  captionPosition="bottom"
  aspectRatio="2/1"
/>

The updated benchmarks table looks like this:

| Model                    | Percentage of Questions Answered Correctly |
| ------------------------ | ------------------------------------------ |
| Sonnet 4                 | 72%                                        |
| Gemini 2.5 Flash Preview | 69%                                        |
| GPT-4o                   | 66%                                        |
| GPT-4.1                  | 51%                                        |
| Qwen 2.5 14B             | 43%                                        |
| ART•S (Qwen 2.5 14B)     | **85%** (42% improvement)                  |

But benchmarks are only one way to measure success. Let's take a look at some of the summaries our model generated.

## Demo Time!

Below you can see a side by side comparison of summaries generated by our model and Sonnet 4 on the documents shown above. Both models do a good job condensing the text, reducing document length by about 70% on average.

But in our opinion, summaries generated by the model we trained are a little more informative. For example, `ART•S` states that Plasti-Cycle was started "by local environmental activist Janelle Avery and high school students on November 15, 2023, in a Mississippi River town", giving readers the who, when, and where in one cohesive statement.

You can also see in the Pine Ridge example how our model has a prefrence for keeping exact quotes, which is probably helpful in answering questions on details.

<ExampleTrajectories />

<p />
If you want to play around generating your own summaries, you can paste a document
in here and compare results between models.

<SummaryDemo />

## **Lessons Learned**

When initially setting up our reward function, we tried giving a slight negative reward for each word generated. However, due to the nature of GRPO, if all the summaries in a rollout are able to answer the same number of questions, then the algorithm will fixate on smaller optimizations like reducing summary length. Prematurely optimizing for short summaries slows the model's progress towards answering more questions. This approach might work in a longer training run, but definitely interfered with our ability to iterate.

Even without this additional reward criterion, our model learned to be more concise. Below you can see the evolution of summary length at each training step. Our goal was to average around 300 words per summary, and by giving no reward when the model exceeded 350 words, we allowed `ART•S` to initially learn to be more verbose before finally settling within a range similar to the other models we benchmarked.

<LightboxImage
  src="/blog-images/summary-rl/word-training-progress.svg"
  alt="Training curve"
  caption="The model stays a healthy distance away from our cutoff so that it always receives some reward."
  captionPosition="bottom"
  aspectRatio="2/1"
/>

This makes intuitive sense. Some of the summaries generated were too long and recieved no reward, so the model learned to avoid generating summaries even close to 350 words long, in case it accidentally went over. Gradually reducing reward once the model passes the cutoff might have resulted in even shorter summaries, but the simpler approach we took worked well enough for our purposes.

## Costs

For the final training run we used a learning rate of 5e-5. We ran the training job using SkyPilot on a Runpod H100.

ART implements several tricks to speed up training, like running rollouts with vLLM, using aggressive sample packing at training time, and building on Unsloth's training efficiency improvements. The end result of these optimizations is faster feedback cycles and cheaper training. The entire training job took around 5 hours to run and fit on a single H100 GPU. This meant the GPU costs were around $15, and after adding in the additional $7 spent on judge tokens, the total cost of the final training run was only **$22**!

## Impact

Reinforcement learning allows you to strictly define the criteria you care about and train your model to better satisfy them. In contrast to prompting alone, models trained using RL get better at satisfying your criteria as you expose them to more training samples.

For just $22 you can define your own criteria for what makes a good summary and train a model that generates state of the art results. By altering the documents, questions and/or reward you can generate a model that:

- Always puts meeting action items at the top
- Doesn't include PII
- Generates summaries that fit in a tweet
- Or whatever goal you care about!

You can apply these same principles to similar problems--ones for which you don't know how to improve performance, but you know what "good" looks like--and RL your way to success. This was the big takeaway from the [DeepSeek paper](https://arxiv.org/abs/2402.03300) in which they used RL to solve hard math problems, but hopefully our summarizer example shows how you can apply it to more day-to-day problems.

## Learn more

Everything in this blog post is open source, and we'd love to see others build on top of our work! The [ART library](https://github.com/OpenPipe/ART) is a great place to start, and you can find all the code for this specific project [here](https://github.com/OpenPipe/summary-rl).

If you'd like to build your own project using ART, join us on [Discord](https://discord.gg/zbBHRUpwf4). And if you're an enterprise interested in working with our team to train your own proprietary agents, [let's chat](mailto:hello@openpipe.ai) and see how we can help!

---

<style jsx>{`
  .alpha-tight {
    list-style-type: lower-alpha;
    margin-left: 1.25rem;
  }
  .alpha-tight > li {
    margin: 0.1rem 0;
  }
  .alpha-tight > li > p {
    margin: 0;
  } /* kill the default <p> gap */
`}</style>
